import { Activity, ArrowRight, Building2, type LucideIcon, Plug, Shield } from "lucide-react";
import { Link } from "react-router";

import { PageHeader } from "@/components/page-header";
import { useHealth, useMe } from "@/lib/api";
import { cn } from "@/lib/utils";

interface QuickLink {
  to: string;
  label: string;
  description: string;
  icon: LucideIcon;
}

const ADMIN_LINKS: QuickLink[] = [
  {
    to: "/admin/accounts",
    label: "Customer accounts",
    description: "Onboard customers, manage contacts, and grant MCP access.",
    icon: Building2,
  },
  {
    to: "/admin/mcp",
    label: "MCP insights",
    description: "Tool calls, denied requests, and the full audit trail.",
    icon: Plug,
  },
  {
    to: "/admin/users",
    label: "User management",
    description: "Invite teammates and manage platform roles.",
    icon: Shield,
  },
  {
    to: "/admin/activity",
    label: "Activity",
    description: "Sign-ins and events across the platform.",
    icon: Activity,
  },
];

export function HomePage() {
  const { data: me } = useMe();
  const { data: health } = useHealth();

  const backendStatus = health?.status ?? "unreachable";
  const isOk = backendStatus === "ok";
  const greetingName = me?.first_name?.trim() || me?.email || "there";
  const isAdmin = me?.role === "admin";

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6">
      <PageHeader
        eyebrow="Welcome back"
        title={greetingName}
        description="Securely connect Johnson & Johnson MedTech data through MCP and power trusted customer-side agents with governed access."
      />

      <div className="bg-card border-border flex items-center gap-2 rounded-lg border px-4 py-3 text-sm shadow-sm">
        <span
          className={cn(
            "inline-block h-2 w-2 rounded-full",
            isOk ? "bg-jj-green-03" : "bg-destructive",
          )}
          aria-hidden
        />
        <span className="text-muted">Backend status</span>
        <span className={cn("font-medium", isOk ? "text-jj-green-03" : "text-destructive")}>
          {backendStatus}
        </span>
      </div>

      {isAdmin && (
        <section className="mt-8 space-y-3">
          <h3 className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
            Jump back in
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {ADMIN_LINKS.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className="group bg-card border-border hover:border-primary/40 flex items-start gap-3 rounded-lg border p-4 shadow-sm transition-colors"
                >
                  <span className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="text-foreground flex items-center gap-1 text-sm font-medium">
                      {link.label}
                      <ArrowRight className="h-3.5 w-3.5 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                    </span>
                    <span className="text-muted mt-1 block text-xs leading-relaxed">
                      {link.description}
                    </span>
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
